import { CONNECT_API_URL } from '../configs/connect';

// Thin POST wrapper for the Amazon Connect support lambda. The lambda
// dispatches on a `{ feature, action, ... }` body shape — recording
// pause/resume, disposition tree fetches, disposition submissions all flow
// through the same endpoint. Documented in the legacy softphone bundle and
// reused here so the browser doesn't need AWS SDK access of its own.

export class ConnectApiError extends Error {
  constructor(
    message: string,
    public readonly status: number | null
  ) {
    super(message);
    this.name = 'ConnectApiError';
  }
}

// Lambda invocations should be sub-second; cap at 30s so a hung connection
// doesn't leave a UI button stuck in "Saving…" state forever.
const CONNECT_API_TIMEOUT_MS = 30_000;

export const postConnectApi = async <T = unknown>(body: Record<string, unknown>): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CONNECT_API_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(CONNECT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ConnectApiError(
        `Connect lambda did not respond within ${CONNECT_API_TIMEOUT_MS / 1000}s`,
        null
      );
    }
    throw new ConnectApiError(
      e instanceof Error ? e.message : 'Network error contacting the Connect lambda',
      null
    );
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new ConnectApiError(`Connect lambda returned ${response.status}`, response.status);
  }

  // The lambda returns JSON for every action that has a body; `pauseresume`
  // returns an empty object on success. Treat malformed JSON as an empty
  // success rather than failing the call.
  try {
    return (await response.json()) as T;
  } catch {
    return {} as T;
  }
};
